// Function index:
//   myParties()                       — parties the logged-in user belongs to.
//   joinParty(invite_code)            — join by invite code.
//   createParty(world_id, name)       — create a new party in a world.
//   fetchBootstrap(partyId)           — initial game state for a party.
//   streamHexes(partyId, q, r, rad)   — Phase B: fetch a disc of hexes + npcs around (q,r).
//   fetchOverworldMap(partyId)        — NM-G2 strategic overworld view.
//   getJournal(partyId)               — my private journal in this party.
//   putJournal(partyId, content)      — upsert my journal text.
//   getQuests(partyId)                — the party's quests (active first).
//   getFactions(partyId)              — the party's faction standings.
//   getLedger(partyId)                — the party's NPC relationship ledger
//     (people/factions/places) for the Ledger panel.
//   getChronicle(partyId)             — recent world + party chronicle entries.
//   turnInQuest(partyId, questId)     — hand a ready quest in at its giver.
//   getBoard(partyId)                 — Guild rank/advancement/exam/hall + postings.
//   acceptPosting(partyId, postingId) — take a board posting; mints a real quest.
//   getShop(partyId)                  — the shop at the acting character's
//     position (or null), wares priced, plus their coin balance.
//   buyItem(partyId, itemName)        — buy a ware; server recomputes price.
//   sellItem(partyId, characterItemId) — sell a carried item by row id.

import { getJson, postJson, putJson } from './http.js';
import type {
  Advancement,
  BoardPosting,
  GuildExamState,
  GuildHall,
  BootstrapResponse, FactionStanding, HexStreamResponse, PartySummary, Quest, StrategicMapResponse,
} from './types.js';

export interface LedgerPerson {
  npc_id: number;
  name: string;
  archetype: string;
  alive: boolean;
  attitude: number;
  summary: string;
  moments: string[];
  met_at: string;
  updated_at: string;
}

export interface LedgerPlace {
  hex_id: number;
  hex_name: string;
  world_name: string;
  kind: string;
  texts: string[];
}

export interface LedgerResponse {
  people: LedgerPerson[];
  factions: FactionStanding[];
  places: LedgerPlace[];
}

export const myParties = (): Promise<{ parties: PartySummary[]; member_cap: number }> => getJson('/api/parties/me');

export const joinParty = (invite_code: string) =>
  postJson<{ party_id: number; already_member: boolean }>('/api/parties/join', { invite_code });

export const createParty = (world_id: number, name: string) =>
  postJson<{ party_id: number; invite_code: string }>('/api/parties', { world_id, name });

export const fetchBootstrap = (partyId: number | string) =>
  getJson<BootstrapResponse>(`/api/parties/${partyId}/bootstrap`);

// Phase B: request a disc of hexes around (q, r) within `radius` (capped
// server-side at 60). Used when the player nears the edge of the
// bootstrap-loaded region, or pans the map far from spawn.
export const streamHexes = (partyId: number | string, q: number, r: number, radius = 30) =>
  getJson<HexStreamResponse>(
    `/api/parties/${partyId}/hexes?around_q=${q}&around_r=${r}&radius=${radius}`,
  );

// NM-G2: discovered hexes + visible doorways on the PARTY'S overworld,
// with an anchor showing where the speaker currently is (overworld
// coord or doorway-hex if they're inside an interior). Powers the
// strategic map overlay.
export const fetchOverworldMap = (partyId: number | string) =>
  getJson<StrategicMapResponse>(`/api/parties/${partyId}/overworld-map`);

export const getJournal = (partyId: number) =>
  getJson<{ content: string; updated_at: string | null }>(`/api/parties/${partyId}/journal`);

export const putJournal = (partyId: number, content: string) =>
  putJson<{ ok: true }>(`/api/parties/${partyId}/journal`, { content });

export const getQuests = (partyId: number | string) =>
  getJson<{ quests: Quest[] }>(`/api/parties/${partyId}/quests`);

export const turnInQuest = (partyId: number, questId: number) =>
  postJson<{ ok: true; xp: number; title: string }>(`/api/parties/${partyId}/quests/${questId}/turn-in`, {});

export const getBoard = (partyId: number | string) =>
  getJson<{ rank: string; merit?: number; advancement: Advancement | null; exam: GuildExamState | null; hall: GuildHall | null; postings: BoardPosting[] }>(`/api/parties/${partyId}/board`);

export const acceptPosting = (partyId: number, postingId: number) =>
  postJson<{ ok: true; quest_id: number }>(`/api/parties/${partyId}/board/${postingId}/accept`, {});

export const getFactions = (partyId: number) =>
  getJson<{ factions: FactionStanding[] }>(`/api/parties/${partyId}/factions`);

export const getLedger = (partyId: number): Promise<LedgerResponse> =>
  getJson(`/api/parties/${partyId}/ledger`);

export interface ChronicleEntry {
  kind: string;
  text: string;
  mine: boolean;
  created_at: string;
}

export const getChronicle = (partyId: number): Promise<{ entries: ChronicleEntry[] }> =>
  getJson(`/api/parties/${partyId}/chronicle`);

export interface ShopWare { name: string; kind: string; description: string; price: number }
export interface ShopPayload {
  shop: { trade: string; name: string; keeper: string | null; wares: ShopWare[] } | null;
  coin: number;
}

export const getShop = (partyId: number | string): Promise<ShopPayload> =>
  getJson(`/api/parties/${partyId}/shop`);

export const buyItem = (partyId: number, itemName: string) =>
  postJson<{ ok: true; coin: number }>(`/api/parties/${partyId}/shop/buy`, { item_name: itemName });

export const sellItem = (partyId: number, characterItemId: number) =>
  postJson<{ ok: true; coin: number; price: number }>(`/api/parties/${partyId}/shop/sell`, { character_item_id: characterItemId });
